import { useEffect, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { getOrgInfo } from '@/services/org/get-org-info';
import { Org } from '@/types';
import { useNotification } from '@/components/notifications';
import { AdminBrandingPrimaryColor } from '@/components/admin/org-branding/admin-branding-primary-color';
import { AdminBrandingLogo } from '@/components/admin/org-branding/admin-branding-logo';
import { AdminBrandingComposerSettings } from '@/components/admin/org-branding/admin-branding-composer-settings';

interface OrgBrandingTabProps {
  orgId: string;
}

export const OrgBrandingTab = ({ orgId }: OrgBrandingTabProps) => {
  const [org, setOrg] = useState<Org | null>(null);
  const [loading, setLoading] = useState(true);
  const notification = useNotification();

  const fetchOrgData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getOrgInfo(orgId);

      if (response?.detail) {
        notification.error(response.detail);
        return;
      }

      if (response?.data) {
        setOrg(response.data as unknown as Org);
      }
    } catch (error) {
      console.error('Error fetching org info:', error);
      notification.error('Failed to load organization branding data');
    } finally {
      setLoading(false);
    }
  }, [orgId, notification]);

  // Update org state locally without refetching
  const handleOrgUpdate = useCallback((updates: Partial<Org>) => {
    setOrg((prevOrg) => {
      if (!prevOrg) return prevOrg;
      return {
        ...prevOrg,
        ...updates,
        branding: {
          ...prevOrg.branding,
          ...updates.branding,
        },
      };
    });
  }, []);

  useEffect(() => {
    if (orgId) {
      fetchOrgData();
    }
  }, [orgId, fetchOrgData]);

  if (loading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        <span className='ml-2 text-muted-foreground'>
          Loading branding settings...
        </span>
      </div>
    );
  }

  if (!org) {
    return (
      <div className='flex items-center justify-center py-12'>
        <p className='text-muted-foreground'>
          Failed to load organization data
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <AdminBrandingPrimaryColor org={org} onUpdate={handleOrgUpdate} />
      <AdminBrandingLogo org={org} onUpdate={handleOrgUpdate} />
      <AdminBrandingComposerSettings org={org} onUpdate={handleOrgUpdate} />
    </div>
  );
};
